import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchArticle, fetchSiblings, reanalyzeArticle } from "../../api";
import { formatDate } from "../../utils/date";

const CATEGORY_LABELS: Record<string, string> = {
  policy_regulation: "政策监管",
  product_update: "产品动态",
  market_trend: "市场趋势",
  company_news: "公司资讯",
  risk_event: "风险事件",
  technology: "技术创新",
};

const SENTIMENT_STYLES: Record<string, { label: string; cls: string }> = {
  positive: { label: "正面", cls: "text-positive bg-positive-light dark:bg-green-900/30 border-green-200 dark:border-green-800" },
  negative: { label: "负面", cls: "text-negative bg-negative-light dark:bg-red-900/30 border-red-200 dark:border-red-800" },
  neutral: { label: "中性", cls: "text-ink-tertiary bg-surface-sunken border-border" },
};

const IMPACT_STYLES: Record<string, string> = {
  critical: "text-rose-700 dark:text-rose-400 bg-rose-50 dark:bg-rose-900/30 border-rose-200 dark:border-rose-800",
  high: "text-orange-700 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/30 border-orange-200 dark:border-orange-800",
  medium: "text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800",
  low: "text-positive bg-positive-light dark:bg-green-900/30 border-green-200 dark:border-green-800",
};

const HEADING_RE = /^(#{1,3}\s+|[一二三四五六七八九十百]+[、．.]\s*|（[一二三四五六七八九十]+）\s*|[（(]\d+[)）]\s*|\d+[.、]\s+)(.+)$/;

function ArticleContent({ text }: { text: string }) {
  const lines = text.split(/\n+/).map((l) => l.trim()).filter(Boolean);
  const elements: React.ReactNode[] = [];
  let paraBuffer: string[] = [];

  const flushPara = () => {
    if (paraBuffer.length === 0) return;
    elements.push(
      <p key={elements.length} className="mb-3">
        {paraBuffer.join(" ")}
      </p>
    );
    paraBuffer = [];
  };

  for (const line of lines) {
    const m = line.match(HEADING_RE);
    if (m) {
      flushPara();
      elements.push(
        <p key={elements.length} className="mt-5 mb-2 font-semibold text-ink">
          {line}
        </p>
      );
    } else if (line.length < 40 && /[：:]$/.test(line)) {
      flushPara();
      elements.push(
        <p key={elements.length} className="mt-4 mb-1 font-medium text-ink">
          {line}
        </p>
      );
    } else {
      paraBuffer.push(line);
      if (paraBuffer.join("").length > 200) {
        flushPara();
      }
    }
  }
  flushPara();

  return <>{elements}</>;
}

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const articleId = Number(id);

  const {
    data: article,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["article", articleId],
    queryFn: () => fetchArticle(articleId),
    enabled: !!articleId,
  });

  // 同系列历年版本（官方披露文件专用）
  const { data: siblings } = useQuery({
    queryKey: ["article-siblings", articleId],
    queryFn: () => fetchSiblings(articleId),
    enabled: !!articleId && !!article?.metadata_?.report_group_key,
  });

  const handleReanalyze = async () => {
    await reanalyzeArticle(articleId);
    setTimeout(() => refetch(), 3000);
  };

  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto animate-pulse space-y-4 py-8">
        <div className="h-7 bg-surface-sunken rounded w-3/4" />
        <div className="h-4 bg-surface-sunken rounded w-1/3" />
        <div className="h-48 bg-surface-sunken rounded mt-6" />
      </div>
    );
  }

  if (isError || !article) {
    return (
      <div className="text-center py-20 text-ink-tertiary text-sm">
        文章不存在或加载失败
        <Link to="/" className="block mt-3 text-primary-500">
          返回首页
        </Link>
      </div>
    );
  }

  const analysis = article.analysis;

  return (
    <div className="max-w-5xl mx-auto">
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 text-xs text-ink-tertiary mb-6">
        <Link to="/news" className="hover:text-primary-500 transition-colors">
          资讯
        </Link>
        <span className="text-ink-faint">/</span>
        <span>{article.source_name}</span>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
        {/* Article */}
        <div
          className={`flex-1 min-w-0 ${
            article.metadata_?.pdf_url ? "" : "max-w-[680px]"
          }`}
        >
          <h1 className="text-xl sm:text-2xl font-semibold text-ink mb-5 leading-[1.4] tracking-tight">
            {article.title}
          </h1>

          <div className="flex flex-wrap items-center gap-3 text-xs text-ink-tertiary mb-6 pb-5 border-b border-border">
            {article.source_name && (
              <span className="font-medium text-ink-secondary">
                {article.source_name}
              </span>
            )}
            {article.published_at && (
              <span className="font-mono tabular-nums">{formatDate(article.published_at)}</span>
            )}
            {article.author && <span>{article.author}</span>}
            {article.metadata_?.is_scanned_pdf && (
              <span className="inline-flex items-center gap-1 px-1.5 py-[3px] rounded bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800 text-[10px] font-medium leading-none tracking-wide">
                ⚠ 扫描版 PDF（暂无文本层）
              </span>
            )}
            {article.metadata_?.pdf_url ? (
              <a
                href={article.metadata_.pdf_url}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-auto inline-flex items-center gap-1 text-primary-500 hover:text-primary-600 text-xs font-medium"
              >
                ↓ 下载 PDF
                {article.metadata_.pdf_size && article.metadata_.pdf_size > 0 && (
                  <span className="text-ink-faint font-mono tabular-nums">
                    ({(article.metadata_.pdf_size / 1024 / 1024).toFixed(1)}M)
                  </span>
                )}
              </a>
            ) : (
              <a
                href={article.url}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-auto text-primary-500 hover:text-primary-600 text-xs"
              >
                原文
              </a>
            )}
          </div>

          {/* Keywords */}
          {article.keywords && article.keywords.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-5">
              {article.keywords.map((kw) => (
                <span
                  key={kw}
                  className="inline-flex items-center text-[10px] leading-none tracking-wide px-1.5 py-[3px] rounded bg-surface-sunken text-ink-tertiary border border-border select-none"
                >
                  <span className="text-ink-faint mr-0.5">#</span>
                  {kw}
                </span>
              ))}
            </div>
          )}

          {/* Content */}
          {article.metadata_?.pdf_url ? (
            // PDF 类文章：直接嵌入浏览器 PDF 阅读器
            <div className="bg-surface-sunken rounded-lg border border-border overflow-hidden">
              <iframe
                src={article.metadata_.pdf_url}
                title={article.title}
                className="w-full bg-white"
                style={{ height: "calc(100vh - 280px)", minHeight: "640px" }}
                loading="lazy"
              />
              <div className="flex items-center justify-between px-3 py-2 text-2xs text-ink-tertiary border-t border-border bg-surface">
                <span>
                  若 PDF 无法预览，请
                  <a
                    href={article.metadata_.pdf_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-1 text-primary-500 hover:text-primary-600 font-medium"
                  >
                    点此下载
                  </a>
                </span>
                {article.metadata_.is_scanned_pdf && (
                  <span className="text-amber-600 dark:text-amber-400">
                    扫描版 PDF
                  </span>
                )}
              </div>
            </div>
          ) : (
            <div className="text-sm text-ink-secondary leading-[1.85]">
              {article.content_text ? (
                <ArticleContent text={article.content_text} />
              ) : (
                <p className="text-ink-tertiary italic">暂无正文内容</p>
              )}
            </div>
          )}
        </div>

        {/* Analysis sidebar */}
        <aside className="w-full lg:w-64 shrink-0">
          <div className="lg:sticky lg:top-16 space-y-3">
            {analysis ? (
              <div className="bg-surface-raised dark:bg-surface-raised rounded-lg border-subtle p-4">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-xs font-semibold text-ink">
                    AI 分析
                  </span>
                  <span className="text-2xs text-ink-tertiary font-mono">
                    {analysis.model_version}
                  </span>
                </div>

                {/* Meta */}
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {analysis.category && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-surface-sunken text-ink-tertiary border border-border font-medium leading-none tracking-wide">
                      {CATEGORY_LABELS[analysis.category] ?? analysis.category}
                    </span>
                  )}
                  {analysis.sentiment && SENTIMENT_STYLES[analysis.sentiment] && (
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded font-medium border leading-none tracking-wide ${
                        SENTIMENT_STYLES[analysis.sentiment].cls
                      }`}
                    >
                      {SENTIMENT_STYLES[analysis.sentiment].label}
                    </span>
                  )}
                </div>

                {/* Importance bar */}
                {analysis.importance_score && (
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-2xs text-ink-tertiary">重要程度</span>
                      <span className="text-2xs font-medium text-ink-secondary font-mono tabular-nums">
                        {analysis.importance_score}/5
                      </span>
                    </div>
                    <div className="flex gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <div
                          key={i}
                          className={`flex-1 h-1.5 rounded-full ${
                            i < analysis.importance_score!
                              ? analysis.importance_score! >= 4
                                ? "bg-warning"
                                : "bg-primary-500"
                              : "bg-surface-sunken"
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Summary */}
                {analysis.summary && (
                  <div className="mb-4">
                    <p className="text-label mb-1">核心摘要</p>
                    <p className="text-sm text-ink-secondary leading-relaxed">
                      {analysis.summary}
                    </p>
                  </div>
                )}

                {/* Key points */}
                {analysis.key_points && analysis.key_points.length > 0 && (
                  <div className="mb-4">
                    <p className="text-label mb-2">关键要点</p>
                    <ul className="space-y-1.5">
                      {analysis.key_points.map((pt, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-1.5 text-sm text-ink-secondary"
                        >
                          <span className="text-accent shrink-0 mt-0.5 leading-none font-medium">
                            ›
                          </span>
                          {pt}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Policy impact */}
                {analysis.policy_impact?.has_policy && (
                  <div className="mb-4 p-3 bg-warning-light dark:bg-amber-900/20 rounded-lg border border-warning/20 dark:border-amber-800">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-2xs font-semibold text-amber-700 dark:text-amber-400">
                        政策影响
                      </span>
                      {analysis.policy_impact.impact_level && (
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded font-medium border leading-none tracking-wide ${
                            IMPACT_STYLES[analysis.policy_impact.impact_level] ?? ""
                          }`}
                        >
                          {analysis.policy_impact.impact_level}
                        </span>
                      )}
                    </div>
                    {analysis.policy_impact.summary && (
                      <p className="text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
                        {analysis.policy_impact.summary}
                      </p>
                    )}
                    {analysis.policy_impact.effective_date && (
                      <p className="text-2xs text-amber-600 dark:text-amber-400 mt-1 font-mono">
                        生效: {analysis.policy_impact.effective_date}
                      </p>
                    )}
                  </div>
                )}

                <button
                  onClick={handleReanalyze}
                  className="w-full text-xs text-ink-tertiary hover:text-ink-secondary transition-colors text-center pt-1"
                >
                  重新分析
                </button>
              </div>
            ) : (
              <div className="bg-surface-raised dark:bg-surface-raised rounded-lg border-subtle p-4 text-center">
                <p className="text-sm text-ink-tertiary mb-2">分析处理中</p>
                <p className="text-xs text-ink-faint">{article.status}</p>
                <button
                  onClick={handleReanalyze}
                  className="mt-3 text-xs text-primary-500 hover:text-primary-600 font-medium"
                >
                  立即分析
                </button>
              </div>
            )}

            {/* 同系列历年版本（官方披露文件专用） */}
            {siblings && siblings.length > 0 && (
              <div className="bg-surface-raised dark:bg-surface-raised rounded-lg border-subtle p-4">
                <p className="text-xs font-semibold text-ink mb-3">历年版本</p>
                <ul className="space-y-2">
                  {siblings.map((s) => (
                    <li key={s.id} className="flex items-start gap-2 text-sm">
                      <span className="shrink-0 mt-[3px] text-ink-faint text-[10px] font-mono tabular-nums w-16">
                        {s.published_at ? formatDate(s.published_at) : "—"}
                      </span>
                      <div className="flex-1 min-w-0">
                        <Link
                          to={`/article/${s.id}`}
                          className="text-ink-secondary hover:text-primary-500 text-xs leading-snug block transition-colors"
                        >
                          {s.title}
                        </Link>
                        <div className="flex items-center gap-2 mt-0.5">
                          {s.is_scanned_pdf && (
                            <span className="text-[9px] text-amber-600 dark:text-amber-400">
                              扫描版
                            </span>
                          )}
                          {s.pdf_url && (
                            <a
                              href={s.pdf_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[10px] text-primary-500 hover:text-primary-600"
                            >
                              ↓ PDF
                            </a>
                          )}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
